import React, { useState, useEffect, useContext } from "react";
import "./PricingPage.css";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCheck,
  faXmark,
  faMapMarkerAlt,
  faPhone,
  faEnvelope,
  faPaperPlane,
  faArrowRight,
  faGem,
  faUsers,
  faRocket,
  faLightbulb,
  faCheckCircle,
  faCalendarCheck,
} from "@fortawesome/free-solid-svg-icons";
import {
  faFacebookF,
  faTwitter,
  faInstagram,
  faGoogle,
  faYoutube,
  faGooglePlay,
} from "@fortawesome/free-brands-svg-icons";
import Navbar from "../../HomePage/Navbar/Navbar";
import Footer from "../../HomePage/Footer/Footer";
import { ThemeContext } from "../../../context/ThemeContext";

const PricingPage = () => {
  const [scrolled, setScrolled] = useState(false);
  const [activeSection, setActiveSection] = useState("pricing");
  const [selectedPlan, setSelectedPlan] = useState(null);
  const { darkMode } = useContext(ThemeContext);

  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 80) {
        setScrolled(true);
      } else {
        setScrolled(false);
      }

      const sections = ["pricing", "features", "faq"];
      const currentPosition = window.scrollY + 300;

      for (const section of sections) {
        const element = document.getElementById(section);
        if (element) {
          const { offsetTop, offsetHeight } = element;
          if (
            currentPosition >= offsetTop &&
            currentPosition < offsetTop + offsetHeight
          ) {
            setActiveSection(section);
            break;
          }
        }
      }
    };

    window.addEventListener("scroll", handleScroll);

    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  // Smooth scroll function
  const handleNavClick = (e, targetId) => {
    e.preventDefault();
    const targetSection = document.getElementById(targetId);
    if (targetSection) {
      const offsetTop =
        targetSection.getBoundingClientRect().top + window.scrollY;
      window.scrollTo({
        top: offsetTop,
        behavior: "smooth",
      });
    }
  };

  // Handle plan selection
  const handlePlanSelect = (plan) => {
    setSelectedPlan(plan);
    // Scroll to the selected plan
    const planElement = document.getElementById(plan);
    if (planElement) {
      planElement.scrollIntoView({ behavior: "smooth" });
    }
  };

  return (
    <div
      className={`pricing-page-container ${darkMode ? "dark-mode" : ""}`}
      style={{ backgroundColor: darkMode ? "#121212" : "#ffffff" }}
    >
      <Navbar
        scrolled={scrolled}
        activeSection={activeSection}
        handleNavClick={handleNavClick}
      />

      {/* Hero Section */}
      <section
        className={`pricing-hero-section ${darkMode ? "dark-mode" : ""}`}
        style={{
          backgroundColor: darkMode
            ? "#1a1a1a"
            : "linear-gradient(135deg, #f8fafb 0%, #e4f3ee 100%)",
        }}
      >
        <div className="pricing-hero-decoration">
          <div className="decoration-dot"></div>
          <div className="decoration-dot"></div>
          <div className="decoration-dot"></div>
          <div className="decoration-dot"></div>
          <div className="decoration-line"></div>
          <div className="decoration-line"></div>
          <div className="decoration-circle"></div>
          <div className="decoration-circle"></div>
        </div>
        <div className="container" style={{ marginTop: "10%" }}>
          <div className="row text-center">
            <div className="col-12">
              <h1
                className={`pricing-hero-title ${
                  darkMode ? "dark-mode-text" : ""
                }`}
                style={{ color: darkMode ? "#f5f5f5" : "#333" }}
              >
                Choose the Right Plan for Your Business
              </h1>
              <p
                className={`pricing-hero-subtitle ${
                  darkMode ? "dark-mode-text-secondary" : ""
                }`}
                style={{ color: darkMode ? "#cccccc" : "#666" }}
              >
                From individuals to large enterprises, we have tailored
                solutions for every business size
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Detailed Pricing Section */}
      <section
        className={`detailed-pricing-section ${darkMode ? "dark-mode" : ""}`}
        id="pricing"
        style={{ backgroundColor: darkMode ? "#121212" : "#ffffff" }}
      >
        <div className="container">
          {/* Free Plan */}
          <div
            className={`detailed-plan-card ${
              darkMode ? "dark-feature-item" : ""
            }`}
            id="free"
            style={{
              backgroundColor: darkMode ? "#1e1e1e" : "#ffffff",
              borderColor: darkMode ? "#333" : "#edf5ff",
              boxShadow: darkMode
                ? "0 5px 15px rgba(0, 0, 0, 0.2)"
                : "0 15px 30px rgba(0, 0, 0, 0.08)",
            }}
          >
            <div className="row">
              <div className="col-lg-4">
                <div className="plan-info">
                  <div
                    className="plan-icon-wrapper"
                    style={{
                      backgroundColor: darkMode
                        ? "rgba(0, 211, 137, 0.15)"
                        : "rgba(0, 211, 137, 0.1)",
                    }}
                  >
                    <FontAwesomeIcon
                      icon={faLightbulb}
                      className="plan-icon"
                      style={{ color: "#00d389" }}
                    />
                  </div>
                  <h2
                    className="plan-title"
                    style={{ color: darkMode ? "#f5f5f5" : "#00d389" }}
                  >
                    Free Plan
                  </h2>
                  <p
                    className={`plan-description ${
                      darkMode ? "dark-mode-text-secondary" : ""
                    }`}
                    style={{ color: darkMode ? "#aaa" : "#666" }}
                  >
                    Ideal for individuals exploring the system
                  </p>
                  <div className="plan-price">
                    <span
                      className="price-amount"
                      style={{ color: darkMode ? "#00d389" : "#333" }}
                    >
                      $0
                    </span>
                    <span
                      className="price-period"
                      style={{ color: darkMode ? "#aaa" : "#666" }}
                    >
                      /month
                    </span>
                  </div>
                  <button
                    className="plan-select-btn"
                    style={{
                      backgroundColor: "#00d389",
                      color: darkMode ? "#121212" : "#ffffff",
                      transition: "all 0.3s ease",
                    }}
                    onMouseOver={(e) =>
                      (e.target.style.backgroundColor = "#00aa6e")
                    }
                    onMouseOut={(e) =>
                      (e.target.style.backgroundColor = "#00d389")
                    }
                  >
                    Select Plan
                  </button>
                </div>
              </div>
              <div className="col-lg-8">
                <div className="plan-features">
                  <h3
                    className={`features-title ${
                      darkMode ? "dark-mode-text" : ""
                    }`}
                    style={{ color: darkMode ? "#f5f5f5" : "#333" }}
                  >
                    Features Included
                  </h3>
                  <div className="features-grid">
                    <div className="feature-item" style={{ textAlign: "left" }}>
                      <FontAwesomeIcon
                        icon={faCheck}
                        className="feature-icon included"
                        style={{ color: "#00d389" }}
                      />
                      <span
                        className="feature-text"
                        style={{
                          color: darkMode ? "#ddd" : "#555",
                          textAlign: "left",
                        }}
                      >
                        1 User only
                      </span>
                    </div>
                    <div className="feature-item" style={{ textAlign: "left" }}>
                      <FontAwesomeIcon
                        icon={faCheck}
                        className="feature-icon included"
                        style={{ color: "#00d389" }}
                      />
                      <span
                        className="feature-text"
                        style={{
                          color: darkMode ? "#ddd" : "#555",
                          textAlign: "left",
                        }}
                      >
                        Basic Sales & Purchase Management
                      </span>
                    </div>
                    <div className="feature-item" style={{ textAlign: "left" }}>
                      <FontAwesomeIcon
                        icon={faCheck}
                        className="feature-icon included"
                        style={{ color: "#00d389" }}
                      />
                      <span
                        className="feature-text"
                        style={{
                          color: darkMode ? "#ddd" : "#555",
                          textAlign: "left",
                        }}
                      >
                        Basic inventory tracking
                      </span>
                    </div>
                    <div className="feature-item" style={{ textAlign: "left" }}>
                      <FontAwesomeIcon
                        icon={faCheck}
                        className="feature-icon included"
                        style={{ color: "#00d389" }}
                      />
                      <span
                        className="feature-text"
                        style={{
                          color: darkMode ? "#ddd" : "#555",
                          textAlign: "left",
                        }}
                      >
                        Create and send invoices & quotes
                      </span>
                    </div>
                    <div className="feature-item" style={{ textAlign: "left" }}>
                      <FontAwesomeIcon
                        icon={faCheck}
                        className="feature-icon included"
                        style={{ color: "#00d389" }}
                      />
                      <span
                        className="feature-text"
                        style={{
                          color: darkMode ? "#ddd" : "#555",
                          textAlign: "left",
                        }}
                      >
                        View purchase orders & bills
                      </span>
                    </div>
                    <div className="feature-item" style={{ textAlign: "left" }}>
                      <FontAwesomeIcon
                        icon={faCheck}
                        className="feature-icon included"
                        style={{ color: "#00d389" }}
                      />
                      <span
                        className="feature-text"
                        style={{
                          color: darkMode ? "#ddd" : "#555",
                          textAlign: "left",
                        }}
                      >
                        Export limited reports (PDF only)
                      </span>
                    </div>
                    <div className="feature-item" style={{ textAlign: "left" }}>
                      <FontAwesomeIcon
                        icon={faCheck}
                        className="feature-icon included"
                        style={{ color: "#00d389" }}
                      />
                      <span
                        className="feature-text"
                        style={{
                          color: darkMode ? "#ddd" : "#555",
                          textAlign: "left",
                        }}
                      >
                        Basic user permission control
                      </span>
                    </div>
                    <div className="feature-item" style={{ textAlign: "left" }}>
                      <FontAwesomeIcon
                        icon={faCheck}
                        className="feature-icon included"
                        style={{ color: "#00d389" }}
                      />
                      <span
                        className="feature-text"
                        style={{
                          color: darkMode ? "#ddd" : "#555",
                          textAlign: "left",
                        }}
                      >
                        Attach documents & add notes
                      </span>
                    </div>
                    <div className="feature-item" style={{ textAlign: "left" }}>
                      <FontAwesomeIcon
                        icon={faXmark}
                        className="feature-icon excluded"
                        style={{ color: darkMode ? "#ff6b6b" : "#dc3545" }}
                      />
                      <span
                        className="feature-text"
                        style={{
                          color: darkMode ? "#ddd" : "#555",
                          textAlign: "left",
                        }}
                      >
                        No access to dashboard, AI features, or advanced
                        analytics
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Pro Plan */}
          <div
            className={`detailed-plan-card ${
              darkMode ? "dark-feature-item" : ""
            }`}
            id="pro"
            style={{
              backgroundColor: darkMode ? "#1e1e1e" : "#ffffff",
              borderColor: darkMode ? "#333" : "#edf5ff",
              boxShadow: darkMode
                ? "0 5px 15px rgba(0, 0, 0, 0.2)"
                : "0 15px 30px rgba(0, 0, 0, 0.08)",
            }}
          >
            <div
              className="plan-badge"
              style={{
                backgroundColor: "#00ab6c",
                color: "#ffffff",
              }}
            >
              Most Popular
            </div>
            <div className="row">
              <div className="col-lg-4">
                <div className="plan-info pro-plan">
                  <div
                    className="plan-icon-wrapper"
                    style={{
                      backgroundColor: darkMode
                        ? "rgba(0, 211, 137, 0.15)"
                        : "rgba(0, 211, 137, 0.1)",
                    }}
                  >
                    <FontAwesomeIcon
                      icon={faGem}
                      className="plan-icon"
                      style={{ color: "#00d389" }}
                    />
                  </div>
                  <h2
                    className="plan-title"
                    style={{ color: darkMode ? "#f5f5f5" : "#00d389" }}
                  >
                    Pro Plan
                  </h2>
                  <p
                    className={`plan-description ${
                      darkMode ? "dark-mode-text-secondary" : ""
                    }`}
                    style={{ color: darkMode ? "#aaa" : "#666" }}
                  >
                    Perfect for solo professionals or small businesses
                  </p>
                  <div className="plan-price">
                    <span className="price-amount" style={{ color: "#00d389" }}>
                      $10
                    </span>
                    <span
                      className="price-period"
                      style={{ color: darkMode ? "#aaa" : "#666" }}
                    >
                      /month per user
                    </span>
                  </div>
                  <button
                    className="plan-select-btn"
                    style={{
                      backgroundColor: "#00d389",
                      color: "#ffffff",
                      transition: "all 0.3s ease",
                    }}
                    onMouseOver={(e) =>
                      (e.target.style.backgroundColor = "#00aa6e")
                    }
                    onMouseOut={(e) =>
                      (e.target.style.backgroundColor = "#00d389")
                    }
                  >
                    Select Plan
                  </button>
                </div>
              </div>
              <div className="col-lg-8">
                <div className="plan-features">
                  <h3
                    className={`features-title ${
                      darkMode ? "dark-mode-text" : ""
                    }`}
                    style={{ color: darkMode ? "#f5f5f5" : "#333" }}
                  >
                    Features Included
                  </h3>
                  <div className="features-grid">
                    <div className="feature-item" style={{ textAlign: "left" }}>
                      <FontAwesomeIcon
                        icon={faCheck}
                        className="feature-icon included"
                        style={{ color: "#00d389" }}
                      />
                      <span
                        className="feature-text"
                        style={{
                          color: darkMode ? "#ddd" : "#555",
                          textAlign: "left",
                        }}
                      >
                        Everything in Free Plan, plus:
                      </span>
                    </div>
                    <div className="feature-item" style={{ textAlign: "left" }}>
                      <FontAwesomeIcon
                        icon={faCheck}
                        className="feature-icon included"
                        style={{ color: "#00d389" }}
                      />
                      <span
                        className="feature-text"
                        style={{
                          color: darkMode ? "#ddd" : "#555",
                          textAlign: "left",
                        }}
                      >
                        Interactive Business Dashboard
                      </span>
                    </div>
                    <div className="feature-item" style={{ textAlign: "left" }}>
                      <FontAwesomeIcon
                        icon={faCheck}
                        className="feature-icon included"
                        style={{ color: "#00d389" }}
                      />
                      <span
                        className="feature-text"
                        style={{
                          color: darkMode ? "#ddd" : "#555",
                          textAlign: "left",
                        }}
                      >
                        Revenue & invoice analytics
                      </span>
                    </div>
                    <div className="feature-item" style={{ textAlign: "left" }}>
                      <FontAwesomeIcon
                        icon={faCheck}
                        className="feature-icon included"
                        style={{ color: "#00d389" }}
                      />
                      <span
                        className="feature-text"
                        style={{
                          color: darkMode ? "#ddd" : "#555",
                          textAlign: "left",
                        }}
                      >
                        Customer aging & payment tracking
                      </span>
                    </div>
                    <div className="feature-item" style={{ textAlign: "left" }}>
                      <FontAwesomeIcon
                        icon={faCheck}
                        className="feature-icon included"
                        style={{ color: "#00d389" }}
                      />
                      <span
                        className="feature-text"
                        style={{
                          color: darkMode ? "#ddd" : "#555",
                          textAlign: "left",
                        }}
                      >
                        Advanced inventory valuation
                      </span>
                    </div>
                    <div className="feature-item" style={{ textAlign: "left" }}>
                      <FontAwesomeIcon
                        icon={faCheck}
                        className="feature-icon included"
                        style={{ color: "#00d389" }}
                      />
                      <span
                        className="feature-text"
                        style={{
                          color: darkMode ? "#ddd" : "#555",
                          textAlign: "left",
                        }}
                      >
                        Export reports in Excel & PDF
                      </span>
                    </div>
                    <div className="feature-item" style={{ textAlign: "left" }}>
                      <FontAwesomeIcon
                        icon={faCheck}
                        className="feature-icon included"
                        style={{ color: "#00d389" }}
                      />
                      <span
                        className="feature-text"
                        style={{
                          color: darkMode ? "#ddd" : "#555",
                          textAlign: "left",
                        }}
                      >
                        AI-powered Suggestions & Insights
                      </span>
                    </div>
                    <div className="feature-item" style={{ textAlign: "left" }}>
                      <FontAwesomeIcon
                        icon={faCheck}
                        className="feature-icon included"
                        style={{ color: "#00d389" }}
                      />
                      <span
                        className="feature-text"
                        style={{
                          color: darkMode ? "#ddd" : "#555",
                          textAlign: "left",
                        }}
                      >
                        Priority support
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Team Plan */}
          <div
            className={`detailed-plan-card ${
              darkMode ? "dark-feature-item" : ""
            }`}
            id="team"
            style={{
              backgroundColor: darkMode ? "#1e1e1e" : "#ffffff",
              borderColor: darkMode ? "#333" : "#edf5ff",
              boxShadow: darkMode
                ? "0 5px 15px rgba(0, 0, 0, 0.2)"
                : "0 15px 30px rgba(0, 0, 0, 0.08)",
            }}
          >
            <div
              className="plan-badge"
              style={{
                backgroundColor: "#00ab6c",
                color: "#ffffff",
              }}
            >
              Best Value
            </div>
            <div className="row">
              <div className="col-lg-4">
                <div className="plan-info team-plan">
                  <div
                    className="plan-icon-wrapper"
                    style={{
                      backgroundColor: darkMode
                        ? "rgba(0, 211, 137, 0.15)"
                        : "rgba(0, 211, 137, 0.1)",
                    }}
                  >
                    <FontAwesomeIcon
                      icon={faUsers}
                      className="plan-icon"
                      style={{ color: "#00d389" }}
                    />
                  </div>
                  <h2
                    className="plan-title"
                    style={{ color: darkMode ? "#f5f5f5" : "#00d389" }}
                  >
                    Team Plan
                  </h2>
                  <p
                    className={`plan-description ${
                      darkMode ? "dark-mode-text-secondary" : ""
                    }`}
                    style={{ color: darkMode ? "#aaa" : "#666" }}
                  >
                    Ideal for growing teams that need real-time collaboration
                  </p>
                  <div className="plan-price">
                    <span className="price-amount" style={{ color: "#00ab6c" }}>
                      $15
                    </span>
                    <span
                      className="price-period"
                      style={{ color: darkMode ? "#aaa" : "#666" }}
                    >
                      /month
                    </span>
                  </div>
                  <p
                    className={`additional-price-info ${
                      darkMode ? "dark-mode-text-secondary" : ""
                    }`}
                    style={{
                      color: darkMode ? "#aaa" : "#666",
                      fontSize: "14px",
                      marginTop: "-10px",
                      marginBottom: "15px",
                    }}
                  >
                    Includes 2 users
                    <br />
                    Each additional user: $10/month
                  </p>
                  <button
                    className="plan-select-btn"
                    style={{
                      backgroundColor: "#00d389",
                      color: "#ffffff",
                      transition: "all 0.3s ease",
                    }}
                    onMouseOver={(e) =>
                      (e.target.style.backgroundColor = "#00aa6e")
                    }
                    onMouseOut={(e) =>
                      (e.target.style.backgroundColor = "#00d389")
                    }
                  >
                    Select Plan
                  </button>
                </div>
              </div>
              <div className="col-lg-8">
                <div className="plan-features">
                  <h3
                    className={`features-title ${
                      darkMode ? "dark-mode-text" : ""
                    }`}
                    style={{ color: darkMode ? "#f5f5f5" : "#333" }}
                  >
                    Features Included
                  </h3>
                  <div className="features-grid">
                    <div className="feature-item" style={{ textAlign: "left" }}>
                      <FontAwesomeIcon
                        icon={faCheck}
                        className="feature-icon included"
                        style={{ color: "#00d389" }}
                      />
                      <span
                        className="feature-text"
                        style={{
                          color: darkMode ? "#ddd" : "#555",
                          textAlign: "left",
                        }}
                      >
                        Everything in Pro Plan, plus:
                      </span>
                    </div>
                    <div className="feature-item" style={{ textAlign: "left" }}>
                      <FontAwesomeIcon
                        icon={faCheck}
                        className="feature-icon included"
                        style={{ color: "#00d389" }}
                      />
                      <span
                        className="feature-text"
                        style={{
                          color: darkMode ? "#ddd" : "#555",
                          textAlign: "left",
                        }}
                      >
                        Multi-user Access with Role-Based Permissions
                      </span>
                    </div>
                    <div className="feature-item" style={{ textAlign: "left" }}>
                      <FontAwesomeIcon
                        icon={faCheck}
                        className="feature-icon included"
                        style={{ color: "#00d389" }}
                      />
                      <span
                        className="feature-text"
                        style={{
                          color: darkMode ? "#ddd" : "#555",
                          textAlign: "left",
                        }}
                      >
                        Approval Workflows for sales & purchases
                      </span>
                    </div>
                    <div className="feature-item" style={{ textAlign: "left" }}>
                      <FontAwesomeIcon
                        icon={faCheck}
                        className="feature-icon included"
                        style={{ color: "#00d389" }}
                      />
                      <span
                        className="feature-text"
                        style={{
                          color: darkMode ? "#ddd" : "#555",
                          textAlign: "left",
                        }}
                      >
                        Internal Comments, Notes & Activity Logs
                      </span>
                    </div>
                    <div className="feature-item" style={{ textAlign: "left" }}>
                      <FontAwesomeIcon
                        icon={faCheck}
                        className="feature-icon included"
                        style={{ color: "#00d389" }}
                      />
                      <span
                        className="feature-text"
                        style={{
                          color: darkMode ? "#ddd" : "#555",
                          textAlign: "left",
                        }}
                      >
                        Shared Dashboards & Team Reporting
                      </span>
                    </div>
                    <div className="feature-item" style={{ textAlign: "left" }}>
                      <FontAwesomeIcon
                        icon={faCheck}
                        className="feature-icon included"
                        style={{ color: "#00d389" }}
                      />
                      <span
                        className="feature-text"
                        style={{
                          color: darkMode ? "#ddd" : "#555",
                          textAlign: "left",
                        }}
                      >
                        Real-Time Collaboration on Invoices & Orders
                      </span>
                    </div>
                    <div className="feature-item" style={{ textAlign: "left" }}>
                      <FontAwesomeIcon
                        icon={faCheck}
                        className="feature-icon included"
                        style={{ color: "#00d389" }}
                      />
                      <span
                        className="feature-text"
                        style={{
                          color: darkMode ? "#ddd" : "#555",
                          textAlign: "left",
                        }}
                      >
                        Centralized Document & Communication Hub
                      </span>
                    </div>
                    <div className="feature-item" style={{ textAlign: "left" }}>
                      <FontAwesomeIcon
                        icon={faCheck}
                        className="feature-icon included"
                        style={{ color: "#00d389" }}
                      />
                      <span
                        className="feature-text"
                        style={{
                          color: darkMode ? "#ddd" : "#555",
                          textAlign: "left",
                        }}
                      >
                        Advanced User Access Control
                      </span>
                    </div>
                    <div className="feature-item" style={{ textAlign: "left" }}>
                      <FontAwesomeIcon
                        icon={faCheck}
                        className="feature-icon included"
                        style={{ color: "#00d389" }}
                      />
                      <span
                        className="feature-text"
                        style={{
                          color: darkMode ? "#ddd" : "#555",
                          textAlign: "left",
                        }}
                      >
                        Team-Level AI Insights & Performance Tracking
                      </span>
                    </div>
                    <div className="feature-item" style={{ textAlign: "left" }}>
                      <FontAwesomeIcon
                        icon={faCheck}
                        className="feature-icon included"
                        style={{ color: "#00d389" }}
                      />
                      <span
                        className="feature-text"
                        style={{
                          color: darkMode ? "#ddd" : "#555",
                          textAlign: "left",
                        }}
                      >
                        Consolidated Team Billing
                      </span>
                    </div>
                    <div className="feature-item" style={{ textAlign: "left" }}>
                      <FontAwesomeIcon
                        icon={faCheck}
                        className="feature-icon included"
                        style={{ color: "#00d389" }}
                      />
                      <span
                        className="feature-text"
                        style={{
                          color: darkMode ? "#ddd" : "#555",
                          textAlign: "left",
                        }}
                      >
                        Chat & Email Support with Priority Response Times
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Enterprise Plan */}
          <div
            className={`detailed-plan-card ${
              darkMode ? "dark-feature-item" : ""
            }`}
            id="enterprise"
            style={{
              backgroundColor: darkMode ? "#1e1e1e" : "#ffffff",
              borderColor: darkMode ? "#333" : "#edf5ff",
              boxShadow: darkMode
                ? "0 5px 15px rgba(0, 0, 0, 0.2)"
                : "0 15px 30px rgba(0, 0, 0, 0.08)",
            }}
          >
            <div className="row">
              <div className="col-lg-4">
                <div className="plan-info enterprise-plan">
                  <div
                    className="plan-icon-wrapper"
                    style={{
                      backgroundColor: darkMode
                        ? "rgba(0, 211, 137, 0.15)"
                        : "rgba(0, 211, 137, 0.1)",
                    }}
                  >
                    <FontAwesomeIcon
                      icon={faRocket}
                      className="plan-icon"
                      style={{ color: "#00d389" }}
                    />
                  </div>
                  <h2
                    className="plan-title"
                    style={{ color: darkMode ? "#f5f5f5" : "#00d389" }}
                  >
                    Enterprise Plan
                  </h2>
                  <p
                    className={`plan-description ${
                      darkMode ? "dark-mode-text-secondary" : ""
                    }`}
                    style={{ color: darkMode ? "#aaa" : "#666" }}
                  >
                    Tailored for large businesses with advanced needs
                  </p>
                  <div className="plan-price">
                    <span className="price-amount" style={{ color: "#00d389" }}>
                      Custom
                    </span>
                    <span
                      className="price-period"
                      style={{ color: darkMode ? "#aaa" : "#666" }}
                    >
                      Pricing
                    </span>
                  </div>
                  <button
                    className="plan-select-btn"
                    style={{
                      backgroundColor: "#00d389",
                      color: "#ffffff",
                      transition: "all 0.3s ease",
                    }}
                    onMouseOver={(e) =>
                      (e.target.style.backgroundColor = "#00aa6e")
                    }
                    onMouseOut={(e) =>
                      (e.target.style.backgroundColor = "#00d389")
                    }
                  >
                    Contact Sales
                  </button>
                </div>
              </div>
              <div className="col-lg-8">
                <div className="plan-features">
                  <h3
                    className={`features-title ${
                      darkMode ? "dark-mode-text" : ""
                    }`}
                    style={{ color: darkMode ? "#f5f5f5" : "#333" }}
                  >
                    Features Included
                  </h3>
                  <div className="features-grid">
                    <div className="feature-item" style={{ textAlign: "left" }}>
                      <FontAwesomeIcon
                        icon={faCheck}
                        className="feature-icon included"
                        style={{ color: "#00d389" }}
                      />
                      <span
                        className="feature-text"
                        style={{
                          color: darkMode ? "#ddd" : "#555",
                          textAlign: "left",
                        }}
                      >
                        Everything in Team Plan, plus:
                      </span>
                    </div>
                    <div className="feature-item" style={{ textAlign: "left" }}>
                      <FontAwesomeIcon
                        icon={faCheck}
                        className="feature-icon included"
                        style={{ color: "#00d389" }}
                      />
                      <span
                        className="feature-text"
                        style={{
                          color: darkMode ? "#ddd" : "#555",
                          textAlign: "left",
                        }}
                      >
                        All Advanced Financial Reports (Balance Sheet, P&L, Tax)
                      </span>
                    </div>
                    <div className="feature-item" style={{ textAlign: "left" }}>
                      <FontAwesomeIcon
                        icon={faCheck}
                        className="feature-icon included"
                        style={{ color: "#00d389" }}
                      />
                      <span
                        className="feature-text"
                        style={{
                          color: darkMode ? "#ddd" : "#555",
                          textAlign: "left",
                        }}
                      >
                        Custom period reporting & variance analysis
                      </span>
                    </div>
                    <div className="feature-item" style={{ textAlign: "left" }}>
                      <FontAwesomeIcon
                        icon={faCheck}
                        className="feature-icon included"
                        style={{ color: "#00d389" }}
                      />
                      <span
                        className="feature-text"
                        style={{
                          color: darkMode ? "#ddd" : "#555",
                          textAlign: "left",
                        }}
                      >
                        Advanced analytics: vendor/customer performance
                      </span>
                    </div>
                    <div className="feature-item" style={{ textAlign: "left" }}>
                      <FontAwesomeIcon
                        icon={faCheck}
                        className="feature-icon included"
                        style={{ color: "#00d389" }}
                      />
                      <span
                        className="feature-text"
                        style={{
                          color: darkMode ? "#ddd" : "#555",
                          textAlign: "left",
                        }}
                      >
                        Live Strategy & Financial Planning Sessions
                      </span>
                    </div>
                    <div className="feature-item" style={{ textAlign: "left" }}>
                      <FontAwesomeIcon
                        icon={faCheck}
                        className="feature-icon included"
                        style={{ color: "#00d389" }}
                      />
                      <span
                        className="feature-text"
                        style={{
                          color: darkMode ? "#ddd" : "#555",
                          textAlign: "left",
                        }}
                      >
                        Full AI module (predictive insights, trend alerts)
                      </span>
                    </div>
                    <div className="feature-item" style={{ textAlign: "left" }}>
                      <FontAwesomeIcon
                        icon={faCheck}
                        className="feature-icon included"
                        style={{ color: "#00d389" }}
                      />
                      <span
                        className="feature-text"
                        style={{
                          color: darkMode ? "#ddd" : "#555",
                          textAlign: "left",
                        }}
                      >
                        API access & integrations
                      </span>
                    </div>
                    <div className="feature-item" style={{ textAlign: "left" }}>
                      <FontAwesomeIcon
                        icon={faCheck}
                        className="feature-icon included"
                        style={{ color: "#00d389" }}
                      />
                      <span
                        className="feature-text"
                        style={{
                          color: darkMode ? "#ddd" : "#555",
                          textAlign: "left",
                        }}
                      >
                        SSO & enterprise-level security
                      </span>
                    </div>
                    <div className="feature-item" style={{ textAlign: "left" }}>
                      <FontAwesomeIcon
                        icon={faCheck}
                        className="feature-icon included"
                        style={{ color: "#00d389" }}
                      />
                      <span
                        className="feature-text"
                        style={{
                          color: darkMode ? "#ddd" : "#555",
                          textAlign: "left",
                        }}
                      >
                        24/7 priority enterprise support
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Comparison Table */}
          <div
            className={`plan-comparison-section ${
              darkMode ? "dark-feature-item" : ""
            }`}
            style={{
              backgroundColor: darkMode ? "#1e1e1e" : "#ffffff",
              borderColor: darkMode ? "#333" : "#edf5ff",
              boxShadow: darkMode
                ? "0 5px 15px rgba(0, 0, 0, 0.2)"
                : "0 15px 30px rgba(0, 0, 0, 0.08)",
              padding: "30px",
              borderRadius: "15px",
              marginTop: "40px",
            }}
          >
            <h2
              className={`comparison-title ${darkMode ? "dark-mode-text" : ""}`}
              style={{
                color: darkMode ? "#f5f5f5" : "#333",
                marginBottom: "25px",
              }}
            >
              Plan Comparison
            </h2>
            <div className="table-responsive">
              <table
                className={`comparison-table ${
                  darkMode ? "dark-mode-table" : ""
                }`}
                style={{
                  borderColor: darkMode ? "#444" : "#dee2e6",
                }}
              >
                <thead>
                  <tr>
                    <th
                      style={{
                        backgroundColor: darkMode ? "#333" : "#f8f9fa",
                        color: darkMode ? "#f5f5f5" : "#333",
                        borderColor: darkMode ? "#444" : "#dee2e6",
                      }}
                    >
                      Feature
                    </th>
                    <th
                      style={{
                        backgroundColor: darkMode ? "#333" : "#f8f9fa",
                        color: darkMode ? "#f5f5f5" : "#333",
                        borderColor: darkMode ? "#444" : "#dee2e6",
                      }}
                    >
                      Free
                    </th>
                    <th
                      style={{
                        backgroundColor: darkMode ? "#333" : "#f8f9fa",
                        color: darkMode ? "#f5f5f5" : "#333",
                        borderColor: darkMode ? "#444" : "#dee2e6",
                      }}
                    >
                      Pro
                    </th>
                    <th
                      style={{
                        backgroundColor: darkMode ? "#333" : "#f8f9fa",
                        color: darkMode ? "#f5f5f5" : "#333",
                        borderColor: darkMode ? "#444" : "#dee2e6",
                      }}
                    >
                      Team
                    </th>
                    <th
                      style={{
                        backgroundColor: darkMode ? "#333" : "#f8f9fa",
                        color: darkMode ? "#f5f5f5" : "#333",
                        borderColor: darkMode ? "#444" : "#dee2e6",
                      }}
                    >
                      Enterprise
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td
                      style={{
                        borderColor: darkMode ? "#444" : "#dee2e6",
                        backgroundColor: darkMode ? "#2d2d2d" : "#ffffff",
                        color: darkMode ? "#f5f5f5" : "#333",
                      }}
                    >
                      Price per user
                    </td>
                    <td
                      style={{
                        borderColor: darkMode ? "#444" : "#dee2e6",
                        backgroundColor: darkMode ? "#2d2d2d" : "#ffffff",
                        color: darkMode ? "#f5f5f5" : "#333",
                      }}
                    >
                      $0
                    </td>
                    <td
                      style={{
                        borderColor: darkMode ? "#444" : "#dee2e6",
                        backgroundColor: darkMode ? "#2d2d2d" : "#ffffff",
                        color: darkMode ? "#f5f5f5" : "#333",
                      }}
                    >
                      $10
                    </td>
                    <td
                      style={{
                        borderColor: darkMode ? "#444" : "#dee2e6",
                        backgroundColor: darkMode ? "#2d2d2d" : "#ffffff",
                        color: darkMode ? "#f5f5f5" : "#333",
                      }}
                    >
                      $15
                    </td>
                    <td
                      style={{
                        borderColor: darkMode ? "#444" : "#dee2e6",
                        backgroundColor: darkMode ? "#2d2d2d" : "#ffffff",
                        color: darkMode ? "#f5f5f5" : "#333",
                      }}
                    >
                      Custom
                    </td>
                  </tr>
                  <tr>
                    <td
                      style={{
                        borderColor: darkMode ? "#444" : "#dee2e6",
                        backgroundColor: darkMode ? "#252525" : "#f9f9f9",
                        color: darkMode ? "#f5f5f5" : "#333",
                      }}
                    >
                      Users
                    </td>
                    <td
                      style={{
                        borderColor: darkMode ? "#444" : "#dee2e6",
                        backgroundColor: darkMode ? "#252525" : "#f9f9f9",
                        color: darkMode ? "#f5f5f5" : "#333",
                      }}
                    >
                      1 user
                    </td>
                    <td
                      style={{
                        borderColor: darkMode ? "#444" : "#dee2e6",
                        backgroundColor: darkMode ? "#252525" : "#f9f9f9",
                        color: darkMode ? "#f5f5f5" : "#333",
                      }}
                    >
                      1 user
                    </td>
                    <td
                      style={{
                        borderColor: darkMode ? "#444" : "#dee2e6",
                        backgroundColor: darkMode ? "#252525" : "#f9f9f9",
                        color: darkMode ? "#f5f5f5" : "#333",
                      }}
                    >
                      Multiple
                    </td>
                    <td
                      style={{
                        borderColor: darkMode ? "#444" : "#dee2e6",
                        backgroundColor: darkMode ? "#252525" : "#f9f9f9",
                        color: darkMode ? "#f5f5f5" : "#333",
                      }}
                    >
                      Unlimited
                    </td>
                  </tr>
                  <tr>
                    <td
                      style={{
                        borderColor: darkMode ? "#444" : "#dee2e6",
                        backgroundColor: darkMode ? "#2d2d2d" : "#ffffff",
                        color: darkMode ? "#f5f5f5" : "#333",
                      }}
                    >
                      Sales & Purchase
                    </td>
                    <td
                      style={{
                        borderColor: darkMode ? "#444" : "#dee2e6",
                        backgroundColor: darkMode ? "#2d2d2d" : "#ffffff",
                        color: darkMode ? "#f5f5f5" : "#333",
                      }}
                    >
                      Basic
                    </td>
                    <td
                      style={{
                        borderColor: darkMode ? "#444" : "#dee2e6",
                        backgroundColor: darkMode ? "#2d2d2d" : "#ffffff",
                        color: darkMode ? "#f5f5f5" : "#333",
                      }}
                    >
                      Advanced
                    </td>
                    <td
                      style={{
                        borderColor: darkMode ? "#444" : "#dee2e6",
                        backgroundColor: darkMode ? "#2d2d2d" : "#ffffff",
                        color: darkMode ? "#f5f5f5" : "#333",
                      }}
                    >
                      Advanced
                    </td>
                    <td
                      style={{
                        borderColor: darkMode ? "#444" : "#dee2e6",
                        backgroundColor: darkMode ? "#2d2d2d" : "#ffffff",
                        color: darkMode ? "#f5f5f5" : "#333",
                      }}
                    >
                      Full Suite
                    </td>
                  </tr>
                  <tr>
                    <td
                      style={{
                        borderColor: darkMode ? "#444" : "#dee2e6",
                        backgroundColor: darkMode ? "#252525" : "#f9f9f9",
                        color: darkMode ? "#f5f5f5" : "#333",
                      }}
                    >
                      Reports
                    </td>
                    <td
                      style={{
                        borderColor: darkMode ? "#444" : "#dee2e6",
                        backgroundColor: darkMode ? "#252525" : "#f9f9f9",
                        color: darkMode ? "#f5f5f5" : "#333",
                      }}
                    >
                      Limited
                    </td>
                    <td
                      style={{
                        borderColor: darkMode ? "#444" : "#dee2e6",
                        backgroundColor: darkMode ? "#252525" : "#f9f9f9",
                        color: darkMode ? "#f5f5f5" : "#333",
                      }}
                    >
                      Exportable
                    </td>
                    <td
                      style={{
                        borderColor: darkMode ? "#444" : "#dee2e6",
                        backgroundColor: darkMode ? "#252525" : "#f9f9f9",
                        color: darkMode ? "#f5f5f5" : "#333",
                      }}
                    >
                      Shared
                    </td>
                    <td
                      style={{
                        borderColor: darkMode ? "#444" : "#dee2e6",
                        backgroundColor: darkMode ? "#252525" : "#f9f9f9",
                        color: darkMode ? "#f5f5f5" : "#333",
                      }}
                    >
                      All Reports
                    </td>
                  </tr>
                  <tr>
                    <td
                      style={{
                        borderColor: darkMode ? "#444" : "#dee2e6",
                        backgroundColor: darkMode ? "#2d2d2d" : "#ffffff",
                        color: darkMode ? "#f5f5f5" : "#333",
                      }}
                    >
                      Dashboard & Analytics
                    </td>
                    <td
                      style={{
                        borderColor: darkMode ? "#444" : "#dee2e6",
                        backgroundColor: darkMode ? "#2d2d2d" : "#ffffff",
                        color: darkMode ? "#f5f5f5" : "#333",
                      }}
                    >
                      <FontAwesomeIcon
                        icon={faXmark}
                        className="feature-icon excluded"
                        style={{ color: darkMode ? "#ff6b6b" : "#dc3545" }}
                      />
                    </td>
                    <td
                      style={{
                        borderColor: darkMode ? "#444" : "#dee2e6",
                        backgroundColor: darkMode ? "#2d2d2d" : "#ffffff",
                        color: darkMode ? "#f5f5f5" : "#333",
                      }}
                    >
                      <FontAwesomeIcon
                        icon={faCheck}
                        className="feature-icon included"
                        style={{ color: "#00d389" }}
                      />
                    </td>
                    <td
                      style={{
                        borderColor: darkMode ? "#444" : "#dee2e6",
                        backgroundColor: darkMode ? "#2d2d2d" : "#ffffff",
                        color: darkMode ? "#f5f5f5" : "#333",
                      }}
                    >
                      <FontAwesomeIcon
                        icon={faCheck}
                        className="feature-icon included"
                        style={{ color: "#00d389" }}
                      />
                    </td>
                    <td
                      style={{
                        borderColor: darkMode ? "#444" : "#dee2e6",
                        backgroundColor: darkMode ? "#2d2d2d" : "#ffffff",
                        color: darkMode ? "#f5f5f5" : "#333",
                      }}
                    >
                      <FontAwesomeIcon
                        icon={faCheck}
                        className="feature-icon included"
                        style={{ color: "#00d389" }}
                      />
                    </td>
                  </tr>
                  <tr>
                    <td
                      style={{
                        borderColor: darkMode ? "#444" : "#dee2e6",
                        backgroundColor: darkMode ? "#252525" : "#f9f9f9",
                        color: darkMode ? "#f5f5f5" : "#333",
                      }}
                    >
                      AI Features
                    </td>
                    <td
                      style={{
                        borderColor: darkMode ? "#444" : "#dee2e6",
                        backgroundColor: darkMode ? "#252525" : "#f9f9f9",
                        color: darkMode ? "#f5f5f5" : "#333",
                      }}
                    >
                      <FontAwesomeIcon
                        icon={faXmark}
                        className="feature-icon excluded"
                        style={{ color: darkMode ? "#ff6b6b" : "#dc3545" }}
                      />
                    </td>
                    <td
                      style={{
                        borderColor: darkMode ? "#444" : "#dee2e6",
                        backgroundColor: darkMode ? "#252525" : "#f9f9f9",
                        color: darkMode ? "#f5f5f5" : "#333",
                      }}
                    >
                      Basic
                    </td>
                    <td
                      style={{
                        borderColor: darkMode ? "#444" : "#dee2e6",
                        backgroundColor: darkMode ? "#252525" : "#f9f9f9",
                        color: darkMode ? "#f5f5f5" : "#333",
                      }}
                    >
                      Basic
                    </td>
                    <td
                      style={{
                        borderColor: darkMode ? "#444" : "#dee2e6",
                        backgroundColor: darkMode ? "#252525" : "#f9f9f9",
                        color: darkMode ? "#f5f5f5" : "#333",
                      }}
                    >
                      Full AI Suite
                    </td>
                  </tr>
                  <tr>
                    <td
                      style={{
                        borderColor: darkMode ? "#444" : "#dee2e6",
                        backgroundColor: darkMode ? "#2d2d2d" : "#ffffff",
                        color: darkMode ? "#f5f5f5" : "#333",
                      }}
                    >
                      Collaboration Tools
                    </td>
                    <td
                      style={{
                        borderColor: darkMode ? "#444" : "#dee2e6",
                        backgroundColor: darkMode ? "#2d2d2d" : "#ffffff",
                        color: darkMode ? "#f5f5f5" : "#333",
                      }}
                    >
                      <FontAwesomeIcon
                        icon={faXmark}
                        className="feature-icon excluded"
                        style={{ color: darkMode ? "#ff6b6b" : "#dc3545" }}
                      />
                    </td>
                    <td
                      style={{
                        borderColor: darkMode ? "#444" : "#dee2e6",
                        backgroundColor: darkMode ? "#2d2d2d" : "#ffffff",
                        color: darkMode ? "#f5f5f5" : "#333",
                      }}
                    >
                      <FontAwesomeIcon
                        icon={faXmark}
                        className="feature-icon excluded"
                        style={{ color: darkMode ? "#ff6b6b" : "#dc3545" }}
                      />
                    </td>
                    <td
                      style={{
                        borderColor: darkMode ? "#444" : "#dee2e6",
                        backgroundColor: darkMode ? "#2d2d2d" : "#ffffff",
                        color: darkMode ? "#f5f5f5" : "#333",
                      }}
                    >
                      <FontAwesomeIcon
                        icon={faCheck}
                        className="feature-icon included"
                        style={{ color: "#00d389" }}
                      />
                    </td>
                    <td
                      style={{
                        borderColor: darkMode ? "#444" : "#dee2e6",
                        backgroundColor: darkMode ? "#2d2d2d" : "#ffffff",
                        color: darkMode ? "#f5f5f5" : "#333",
                      }}
                    >
                      <FontAwesomeIcon
                        icon={faCheck}
                        className="feature-icon included"
                        style={{ color: "#00d389" }}
                      />
                    </td>
                  </tr>
                  <tr>
                    <td
                      style={{
                        borderColor: darkMode ? "#444" : "#dee2e6",
                        backgroundColor: darkMode ? "#252525" : "#f9f9f9",
                        color: darkMode ? "#f5f5f5" : "#333",
                      }}
                    >
                      Live Financial Consultations
                    </td>
                    <td
                      style={{
                        borderColor: darkMode ? "#444" : "#dee2e6",
                        backgroundColor: darkMode ? "#252525" : "#f9f9f9",
                        color: darkMode ? "#f5f5f5" : "#333",
                      }}
                    >
                      <FontAwesomeIcon
                        icon={faXmark}
                        className="feature-icon excluded"
                        style={{ color: darkMode ? "#ff6b6b" : "#dc3545" }}
                      />
                    </td>
                    <td
                      style={{
                        borderColor: darkMode ? "#444" : "#dee2e6",
                        backgroundColor: darkMode ? "#252525" : "#f9f9f9",
                        color: darkMode ? "#f5f5f5" : "#333",
                      }}
                    >
                      <FontAwesomeIcon
                        icon={faXmark}
                        className="feature-icon excluded"
                        style={{ color: darkMode ? "#ff6b6b" : "#dc3545" }}
                      />
                    </td>
                    <td
                      style={{
                        borderColor: darkMode ? "#444" : "#dee2e6",
                        backgroundColor: darkMode ? "#252525" : "#f9f9f9",
                        color: darkMode ? "#f5f5f5" : "#333",
                      }}
                    >
                      <FontAwesomeIcon
                        icon={faXmark}
                        className="feature-icon excluded"
                        style={{ color: darkMode ? "#ff6b6b" : "#dc3545" }}
                      />
                    </td>
                    <td
                      style={{
                        borderColor: darkMode ? "#444" : "#dee2e6",
                        backgroundColor: darkMode ? "#252525" : "#f9f9f9",
                        color: darkMode ? "#f5f5f5" : "#333",
                      }}
                    >
                      <FontAwesomeIcon
                        icon={faCheck}
                        className="feature-icon included"
                        style={{ color: "#00d389" }}
                      />
                    </td>
                  </tr>
                  <tr>
                    <td
                      style={{
                        borderColor: darkMode ? "#444" : "#dee2e6",
                        backgroundColor: darkMode ? "#2d2d2d" : "#ffffff",
                        color: darkMode ? "#f5f5f5" : "#333",
                      }}
                    >
                      Support
                    </td>
                    <td
                      style={{
                        borderColor: darkMode ? "#444" : "#dee2e6",
                        backgroundColor: darkMode ? "#2d2d2d" : "#ffffff",
                        color: darkMode ? "#f5f5f5" : "#333",
                      }}
                    >
                      Basic Email
                    </td>
                    <td
                      style={{
                        borderColor: darkMode ? "#444" : "#dee2e6",
                        backgroundColor: darkMode ? "#2d2d2d" : "#ffffff",
                        color: darkMode ? "#f5f5f5" : "#333",
                      }}
                    >
                      Priority
                    </td>
                    <td
                      style={{
                        borderColor: darkMode ? "#444" : "#dee2e6",
                        backgroundColor: darkMode ? "#2d2d2d" : "#ffffff",
                        color: darkMode ? "#f5f5f5" : "#333",
                      }}
                    >
                      Chat & Email
                    </td>
                    <td
                      style={{
                        borderColor: darkMode ? "#444" : "#dee2e6",
                        backgroundColor: darkMode ? "#2d2d2d" : "#ffffff",
                        color: darkMode ? "#f5f5f5" : "#333",
                      }}
                    >
                      24/7 Dedicated
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          {/* FAQ Section */}
          <div
            className={`faq-section ${darkMode ? "dark-mode" : ""}`}
            id="faq"
            style={{ marginTop: "60px", marginBottom: "40px" }}
          >
            <h2
              className={`faq-title ${darkMode ? "dark-mode-text" : ""}`}
              style={{
                color: darkMode ? "#f5f5f5" : "#333",
                marginBottom: "30px",
                textAlign: "center",
                fontSize: "36px",
                fontWeight: "700",
              }}
            >
              Frequently Asked Questions
            </h2>
            <div className="faq-container">
              <div
                className={`faq-item ${darkMode ? "dark-feature-item" : ""}`}
                style={{
                  backgroundColor: darkMode ? "#1e1e1e" : "#ffffff",
                  borderColor: darkMode ? "#333" : "#edf5ff",
                  boxShadow: darkMode
                    ? "0 5px 15px rgba(0, 0, 0, 0.2)"
                    : "0 15px 30px rgba(0, 0, 0, 0.08)",
                  padding: "25px",
                  borderRadius: "15px",
                  marginBottom: "20px",
                }}
              >
                <h3
                  className={`faq-question ${darkMode ? "dark-mode-text" : ""}`}
                  style={{
                    color: darkMode ? "#00d389" : "#00d389",
                    marginBottom: "15px",
                    fontSize: "20px",
                    fontWeight: "600",
                  }}
                >
                  Can I upgrade or downgrade my plan anytime?
                </h3>
                <p
                  className={`faq-answer ${
                    darkMode ? "dark-mode-text-secondary" : ""
                  }`}
                  style={{ color: darkMode ? "#aaa" : "#666" }}
                >
                  Yes, you can upgrade, downgrade, or cancel your subscription
                  at any time. Changes to your subscription will be effective
                  immediately or at the end of your current billing period,
                  depending on the type of change.
                </p>
              </div>
              <div
                className={`faq-item ${darkMode ? "dark-feature-item" : ""}`}
                style={{
                  backgroundColor: darkMode ? "#1e1e1e" : "#ffffff",
                  borderColor: darkMode ? "#333" : "#edf5ff",
                  boxShadow: darkMode
                    ? "0 5px 15px rgba(0, 0, 0, 0.2)"
                    : "0 15px 30px rgba(0, 0, 0, 0.08)",
                  padding: "25px",
                  borderRadius: "15px",
                  marginBottom: "20px",
                }}
              >
                <h3
                  className={`faq-question ${darkMode ? "dark-mode-text" : ""}`}
                  style={{
                    color: darkMode ? "#00d389" : "#00d389",
                    marginBottom: "15px",
                    fontSize: "20px",
                    fontWeight: "600",
                  }}
                >
                  Is there a setup fee?
                </h3>
                <p
                  className={`faq-answer ${
                    darkMode ? "dark-mode-text-secondary" : ""
                  }`}
                  style={{ color: darkMode ? "#aaa" : "#666" }}
                >
                  No, there are no setup fees for any of our plans. You only pay
                  the monthly subscription fee for your chosen plan.
                </p>
              </div>
              <div
                className={`faq-item ${darkMode ? "dark-feature-item" : ""}`}
                style={{
                  backgroundColor: darkMode ? "#1e1e1e" : "#ffffff",
                  borderColor: darkMode ? "#333" : "#edf5ff",
                  boxShadow: darkMode
                    ? "0 5px 15px rgba(0, 0, 0, 0.2)"
                    : "0 15px 30px rgba(0, 0, 0, 0.08)",
                  padding: "25px",
                  borderRadius: "15px",
                  marginBottom: "20px",
                }}
              >
                <h3
                  className={`faq-question ${darkMode ? "dark-mode-text" : ""}`}
                  style={{
                    color: darkMode ? "#00d389" : "#00d389",
                    marginBottom: "15px",
                    fontSize: "20px",
                    fontWeight: "600",
                  }}
                >
                  Can I try before I buy?
                </h3>
                <p
                  className={`faq-answer ${
                    darkMode ? "dark-mode-text-secondary" : ""
                  }`}
                  style={{ color: darkMode ? "#aaa" : "#666" }}
                >
                  Absolutely! You can start with our Free plan to test the
                  system, or request a demo of our premium features before
                  deciding to upgrade.
                </p>
              </div>
              <div
                className={`faq-item ${darkMode ? "dark-feature-item" : ""}`}
                style={{
                  backgroundColor: darkMode ? "#1e1e1e" : "#ffffff",
                  borderColor: darkMode ? "#333" : "#edf5ff",
                  boxShadow: darkMode
                    ? "0 5px 15px rgba(0, 0, 0, 0.2)"
                    : "0 15px 30px rgba(0, 0, 0, 0.08)",
                  padding: "25px",
                  borderRadius: "15px",
                  marginBottom: "20px",
                }}
              >
                <h3
                  className={`faq-question ${darkMode ? "dark-mode-text" : ""}`}
                  style={{
                    color: darkMode ? "#00d389" : "#00d389",
                    marginBottom: "15px",
                    fontSize: "20px",
                    fontWeight: "600",
                  }}
                >
                  How does billing work?
                </h3>
                <p
                  className={`faq-answer ${
                    darkMode ? "dark-mode-text-secondary" : ""
                  }`}
                  style={{ color: darkMode ? "#aaa" : "#666" }}
                >
                  We bill monthly or annually, depending on your preference.
                  Annual subscriptions come with a discount. All major credit
                  cards are accepted.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Call to Action */}
      <section
        className={`cta-section ${darkMode ? "dark-mode" : ""}`}
        style={{
          backgroundColor: darkMode ? "#121212" : "#f8f9fa",
          padding: "70px 0",
          position: "relative",
          overflow: "hidden",
        }}
      >
        <div className="cta-background-shapes">
          <div className="shape shape-1"></div>
          <div className="shape shape-2"></div>
          <div className="shape shape-3"></div>
          <div className="shape shape-4"></div>
        </div>
        <div className="container position-relative">
          <div className="row">
            <div className="col-lg-10 offset-lg-1 text-center">
              <div
                className="cta-badge"
                style={{
                  backgroundColor: darkMode
                    ? "rgba(0, 211, 137, 0.1)"
                    : "#00d389",
                  color: darkMode ? "#00d389" : "#ffffff",
                  display: "inline-block",
                  padding: "6px 15px",
                  borderRadius: "30px",
                  fontSize: "14px",
                  fontWeight: "600",
                  marginBottom: "25px",
                  border: darkMode ? "1px solid #00d389" : "none",
                }}
              >
                <span>Limited Time Offer</span>
              </div>
              <h2
                className={`cta-title ${darkMode ? "dark-mode-text" : ""}`}
                style={{
                  color: darkMode ? "#f5f5f5" : "#333",
                  fontSize: "36px",
                  fontWeight: "700",
                  marginBottom: "20px",
                }}
              >
                Ready to streamline your accounting?
              </h2>
              <div className="cta-divider">
                <span
                  style={{
                    display: "block",
                    width: "80px",
                    height: "3px",
                    backgroundColor: darkMode
                      ? "rgba(0, 211, 137, 0.3)"
                      : "#00d389",
                    margin: "0 auto 25px",
                  }}
                ></span>
              </div>
              <p
                className={`cta-text ${
                  darkMode ? "dark-mode-text-secondary" : ""
                }`}
                style={{
                  color: darkMode ? "#aaa" : "#666",
                  marginBottom: "30px",
                  maxWidth: "700px",
                  margin: "0 auto 30px",
                }}
              >
                Start with our Free plan today or contact our sales team for a
                personalized demonstration. Get full access to all features for
                14 days!
              </p>
              <div className="cta-buttons" style={{ marginBottom: "30px" }}>
                <button
                  className="cta-btn primary-btn"
                  style={{
                    backgroundColor: darkMode ? "#1e1e1e" : "#00d389",
                    color: darkMode ? "#ffffff" : "#ffffff",
                    border: darkMode ? "1px solid #00d389" : "none",
                    borderRadius: "50px",
                    padding: "12px 30px",
                    fontSize: "16px",
                    fontWeight: "600",
                    marginRight: "15px",
                    transition: "all 0.3s ease",
                  }}
                >
                  <FontAwesomeIcon
                    icon={faRocket}
                    className="btn-icon"
                    style={{ marginRight: "8px" }}
                  />
                  Get Started Free
                </button>
                <button
                  className="cta-btn secondary-btn"
                  style={{
                    backgroundColor: darkMode ? "#1e1e1e" : "#ffffff",
                    color: darkMode ? "#f5f5f5" : "#333",
                    border: darkMode ? "1px solid #333" : "1px solid #dee2e6",
                    borderRadius: "50px",
                    padding: "12px 30px",
                    fontSize: "16px",
                    fontWeight: "600",
                    transition: "all 0.3s ease",
                  }}
                >
                  <FontAwesomeIcon
                    icon={faCalendarCheck}
                    className="btn-icon"
                    style={{ marginRight: "8px" }}
                  />
                  Request Demo
                </button>
              </div>
              <div
                className="cta-features"
                style={{
                  display: "flex",
                  justifyContent: "center",
                  gap: "25px",
                }}
              >
                <div
                  className="feature-item"
                  style={{
                    display: "flex",
                    alignItems: "center",
                    backgroundColor: darkMode
                      ? "rgba(30, 30, 30, 0.7)"
                      : "rgba(255, 255, 255, 0.7)",
                    padding: "10px 20px",
                    borderRadius: "50px",
                    border: darkMode ? "1px solid #333" : "none",
                  }}
                >
                  <FontAwesomeIcon
                    icon={faCheckCircle}
                    className="feature-icon"
                    style={{
                      color: darkMode ? "#00d389" : "#00d389",
                      marginRight: "8px",
                    }}
                  />
                  <span
                    className={darkMode ? "dark-mode-text" : ""}
                    style={{ color: darkMode ? "#f5f5f5" : "#333" }}
                  >
                    No credit card required
                  </span>
                </div>
                <div
                  className="feature-item"
                  style={{
                    display: "flex",
                    alignItems: "center",
                    backgroundColor: darkMode
                      ? "rgba(30, 30, 30, 0.7)"
                      : "rgba(255, 255, 255, 0.7)",
                    padding: "10px 20px",
                    borderRadius: "50px",
                    border: darkMode ? "1px solid #333" : "none",
                  }}
                >
                  <FontAwesomeIcon
                    icon={faCheckCircle}
                    className="feature-icon"
                    style={{
                      color: darkMode ? "#00d389" : "#00d389",
                      marginRight: "8px",
                    }}
                  />
                  <span
                    className={darkMode ? "dark-mode-text" : ""}
                    style={{ color: darkMode ? "#f5f5f5" : "#333" }}
                  >
                    Free 14-day trial
                  </span>
                </div>
                <div
                  className="feature-item"
                  style={{
                    display: "flex",
                    alignItems: "center",
                    backgroundColor: darkMode
                      ? "rgba(30, 30, 30, 0.7)"
                      : "rgba(255, 255, 255, 0.7)",
                    padding: "10px 20px",
                    borderRadius: "50px",
                    border: darkMode ? "1px solid #333" : "none",
                  }}
                >
                  <FontAwesomeIcon
                    icon={faCheckCircle}
                    className="feature-icon"
                    style={{
                      color: darkMode ? "#00d389" : "#00d389",
                      marginRight: "8px",
                    }}
                  />
                  <span
                    className={darkMode ? "dark-mode-text" : ""}
                    style={{ color: darkMode ? "#f5f5f5" : "#333" }}
                  >
                    Cancel anytime
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default PricingPage;
